import { inject, Injectable } from "@angular/core";
import {
  AttachedMedia,
  AttachedMediaFile,
  Comment,
  CommentLocation,
  Contact,
  DdnData
} from "@orion2/models/tocitem.models";
import { UniversalFile } from "@orion2/models/file.models";
import { Store } from "@viewer/core";
import { DownloadFileService } from "libs/download/download-file.service";
import { MimeType } from "@orion2/models/enums";
import { DatePipe } from "@angular/common";
import { getIncrementalPackageId, getMimeType } from "@orion2/utils/functions.utils";
import { Unzipped } from "fflate";
import { v4 as uuidv4 } from "uuid";
import { CommentService } from "@viewer/toc-items/comments-module/services/comment.service";
import {
  COMMENT_ERRORS,
  CommentError,
  CUSTOM_FILE_ERRORS,
  DDN_ERRORS,
  DdnError,
  GENERAL_ERRORS
} from "@orion2/utils/errors.utils";
import { TranslateService } from "@ngx-translate/core";
import { ImportResultService } from "libs/components/import-result-dialog/import-result.service";
import { AttachedMediaService } from "@viewer/core/toc-items/attached-media.service";

export interface ImportCommentResponse {
  importsOk: string[];
  importsKo: Map<string, string>;
  ddnCodes: string[];
}

@Injectable({
  providedIn: "root"
})
export class CommentImportExportService {
  private readonly attachedMediaService = inject(AttachedMediaService);
  private readonly commentService = inject(CommentService);
  private readonly datePipe = inject(DatePipe);
  private readonly downloadService = inject(DownloadFileService);
  private readonly importResultSvc = inject(ImportResultService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);

  private readonly commentCodeAttributesChecks = new Map<string, (value: string) => boolean>([
    ["modelIdentCode", (value: string) => /^\w{2,14}$/.test(value)],
    ["senderIdent", (value: string) => /^\w{5}$/.test(value)],
    ["yearOfDataIssue", (value: string) => /^\d{4}$/.test(value)],
    ["seqNumber", (value: string) => /^\d{5}$/.test(value)],
    ["commentType", (value: string) => /^[qir]$/.test(value.toLowerCase())]
  ]);
  private readonly domParser = new DOMParser();
  private readonly mediaNameRegex = /.*-(\d{2})\.(\w+)/;
  private readonly textDecoder = new TextDecoder();

  ////////////////////////////////////////////////////////////
  ////////////////////// IMPORT SECTION //////////////////////
  ////////////////////////////////////////////////////////////

  /**
   * Imports a list of comments from an unzipped archive.
   * There are three categories of files:
   * - Comments: every XML file except the DDN one.
   * - DDN: optional XML file that must be unique. This file lists the authorised files for the importation.
   * - Media: every other type of file.
   *
   * @param {Unzipped} files The files to import as comments, their media,
   * and optionally a DDN.
   */
  public async importComments(files: Unzipped): Promise<void> {
    const filenames = Object.keys(files).filter((filename: string) => !filename.includes("/"));

    const hasAtLeastOneComment = filenames.some((filename: string) =>
      /^(?!DDN).*\.xml$/.test(filename)
    );
    if (!hasAtLeastOneComment) {
      throw new Error(CUSTOM_FILE_ERRORS.XML_ERROR);
    }

    let ddnList: Set<string>;
    try {
      ddnList = this.getDdnList(filenames, files);
    } catch (error) {
      if (error instanceof DdnError) {
        throw error;
      } else {
        throw new Error(GENERAL_ERRORS.TECHNICAL);
      }
    }

    // Comments + media.
    const filenamesInZip = new Set(
      filenames.filter((filename: string) => !filename.startsWith("DDN"))
    );

    const filenamesToImport = ddnList ?? filenamesInZip;

    for (const fileToImport of filenamesToImport) {
      const isComment = fileToImport.endsWith(".xml");
      if (isComment) {
        try {
          await this.importComment(fileToImport, files, filenamesInZip, ddnList);
        } catch (error) {
          if (error instanceof CommentError) {
            if (error.message === COMMENT_ERRORS.NOT_IN_ZIP) {
              this.importResultSvc.warning(
                this.translate.instant(error.message, {
                  filename: fileToImport
                })
              );
            } else {
              this.importResultSvc.error(
                this.translate.instant(error.message, {
                  commentFilename: fileToImport,
                  commentCode: error.code
                })
              );
            }
          } else {
            this.importResultSvc.error(this.translate.instant(GENERAL_ERRORS.TECHNICAL));
          }
        } finally {
          filenamesInZip.delete(fileToImport);
        }
      } else {
        const linkedComment = fileToImport.replace(/(-\d+\..*)/, ".xml");
        /*
          If the current media is not linked to any comment, and is absent
          from the ZIP, we send a warning.
          If it is linked to a comment, the warning is handled when importing
          the comment.
        */
        if (!filenamesToImport.has(linkedComment) && !filenamesInZip.has(fileToImport)) {
          this.importResultSvc.warning(
            this.translate.instant("comments.import.warning.ddn.notInZip", {
              filename: fileToImport
            })
          );
        }
        filenamesInZip.delete(fileToImport);
      }
    }

    /*
      In the ZIP, not in the DDN.
      If there is no DDN, filesnamesInZip should have been emptied by now.
     */
    filenamesInZip.forEach((filename: string) => {
      this.importResultSvc.error(
        this.translate.instant("comments.import.warning.ddn.notInDdn", {
          filename
        })
      );
    });

    this.importResultSvc.openDialog(false);
    this.attachedMediaService.resetCachedFiles();
    this.commentService.refresh();
  }

  /**
   * Init a comment from an XML document.
   *
   * @param xml
   * @throws `CommentError`:
   * - `COMMENT_ERRORS.INVALID_STRUCTURE` if the XML or the comment code
   * are invalid.
   * - `COMMENT_ERRORS.ALREADY_EXISTS` if the comment already exists.
   * - `COMMENT_ERRORS.INVALID_PUB` if the comment is on the wrong
   * publication.
   * - `COMMENT_ERRORS.MISSING_DM` if the DM of the comment does not
   * exist **(only for DMs)** (by error propagation).
   * - `COMMENT_ERRORS.MISSING_PM` if the DM of the comment does not
   * exist **(only for PMs)** (by error propagation).
   * @returns {Comment} The comment created from the XML.
   */
  private initComment(xml: Document): Comment {
    const codeTag = xml.querySelector("commentCode");
    let code = "";
    try {
      code = this.constructCommentCode(codeTag);
    } catch (error) {
      throw new CommentError(COMMENT_ERRORS.INVALID_STRUCTURE);
    }

    if (this.commentService.isDuplicateCode(null, code)) {
      throw new CommentError(COMMENT_ERRORS.ALREADY_EXISTS, code);
    }

    const [model, cageCode, year, seqNumber, commentType] = code.split("-");
    if (model !== this.store.pubInfo.model) {
      throw new CommentError(COMMENT_ERRORS.INVALID_PUB, code);
    }

    const title = xml.querySelector("commentTitle")?.textContent || "-";

    const simpleParas = Array.from(xml.querySelectorAll("simplePara"))
      .map((simplePara: Element) => simplePara.textContent)
      .filter((textContent: string) => /\.*\S+.*/.test(textContent));
    const contents = simpleParas.length ? simpleParas : ["-"];

    const dateElement = xml.querySelector("commentAddressItems > issueDate");
    let date: Date;
    if (!dateElement) {
      date = new Date(
        `${dateElement.getAttribute("year")}/${dateElement.getAttribute("month")}/${dateElement.getAttribute("day")}`
      );
    } else {
      date = new Date();
    }

    const priority =
      xml.querySelector("commentPriority")?.getAttribute("commentPriorityCode") ?? "cp01";

    const location = this.getLocation(xml);

    const responseType =
      xml.querySelector("commentResponse")?.getAttribute("responseType") || "default";

    const comment: Comment = {
      _id: `comment_${uuidv4()}`,
      author: this.store.user.userName,
      cageCode,
      code,
      commentType: commentType.toLowerCase(),
      contents,
      date,
      location,
      locationData: "",
      model,
      priority,
      reference: "",
      revision: this.store.publicationRevision,
      seqNumber,
      title,
      type: "comment",
      userId: this.store.user.samAccountName, // SPEC: We import as the author.
      year,
      responseType
    };

    try {
      this.setLocationInfo(xml, comment);
    } catch (error) {
      error.code = code;
      throw error;
    }

    return comment;
  }

  /**
   * Import a comment from its XML.
   * Also imports its linked media.
   *
   * @param {string} commentFilename The filename of the comment to import.
   * @param {Unzipped} files All the files in the import process.
   * @param {Set<string>} filenamesInZip The list of filenames in the ZIP.
   * @param {Set<string>} [ddnList] The list of filenames in the DDN.
   *
   * @throws `COMMENT_ERRORS.NOT_IN_ZIP` if the comment to import is not in the ZIP.
   *
   * @private
   */
  private importComment(
    commentFilename: string,
    files: Unzipped,
    filenamesInZip: Set<string>,
    ddnList?: Set<string>
  ): Promise<void> {
    if (ddnList && !filenamesInZip.has(commentFilename)) {
      throw new CommentError(COMMENT_ERRORS.NOT_IN_ZIP);
    }

    const xmlString: string = this.textDecoder.decode(files[commentFilename]).trim();
    const xml: Document = this.domParser.parseFromString(xmlString, "text/xml");

    if (!this.checkXmlIsValid(xml)) {
      throw new CommentError(COMMENT_ERRORS.INVALID_STRUCTURE);
    }
    const comment = this.initComment(xml);

    const attachmentRefs = Array.from(xml.querySelectorAll("attachmentRef"));

    return (attachmentRefs || [])
      .reduce((acc: Promise<Comment>, attachmentRef: Element) => {
        const attachmentFilename = commentFilename.replace(
          /(.*).xml$/,
          `$1-${attachmentRef.getAttribute("attachmentNumber")}.${attachmentRef.getAttribute("fileExtension")}`
        );
        filenamesInZip.delete(attachmentFilename);

        return acc.then((updatedComment: Comment) => {
          if (ddnList && !ddnList.has(attachmentFilename)) {
            this.importResultSvc.error(
              this.translate.instant("comments.import.warning.ddn.notInDdn", {
                filename: attachmentFilename
              })
            );
            return updatedComment;
          }

          return this.importAttachedMedia(updatedComment, attachmentFilename, files);
        });
      }, Promise.resolve(comment))
      .then((updatedComment: Comment) => this.commentService.saveComment(updatedComment))
      .then((res: boolean) => {
        if (res) {
          this.importResultSvc.success(
            this.translate.instant("comments.import.success", {
              commentCode: comment.code
            })
          );
        } else {
          this.importResultSvc.error(
            `${commentFilename} ${this.translate.instant(GENERAL_ERRORS.TECHNICAL)}`
          );
        }
      })
      .catch((err: CommentError) =>
        this.importResultSvc.error(
          this.translate.instant(err.message, {
            commentFilename: commentFilename,
            commentCode: err.code
          })
        )
      );
  }

  /**
   * Imports the DDN from an XML file.
   *
   * @param {Uint8Array} file The DDN file to import.
   * @throws `DDN_ERRORS.INVALID_STRUCTURE` if the structure of the DDN is wrong.
   * @returns {Document} The DDN file.
   * @private
   */
  private importDdn(file: Uint8Array): Document {
    const xmlString: string = this.textDecoder.decode(file).trim();
    const xml: Document = this.domParser.parseFromString(xmlString, "text/xml");

    if (!this.checkXmlIsValid(xml, true)) {
      throw new DdnError(DDN_ERRORS.INVALID_STRUCTURE);
    }

    const ddnCodeTag = xml.querySelector("ddnCode");
    const ddnCode = this.constructDdnCode(ddnCodeTag);
    this.importResultSvc.success(
      this.translate.instant("comments.import.ddn.success", {
        ddnCode
      })
    );
    return xml;
  }

  /**
   * Gets the files listed in the DDN. These files are the ones expected to be
   * found in the ZIP when importing comments.
   * The returned list includes both comments (XML files) and media (other
   * files), minus the DDN file itself.
   *
   * @param {string} filenames All the names of the files in the ZIP.
   * @param {Unzipped} files The files in the ZIP.
   *
   * @throws `DDN_ERRORS.INVALID_STRUCTURE` if the structure of the DDN is invalid.
   * @throws `DDN_ERRORS.MULTIPLE_DDN` if there is more than one DDN.
   *
   * @returns {Set<string>} The list of files listed in the DDN.
   */
  private getDdnList(filenames: string[], files: Unzipped): Set<string> {
    const ddns = filenames.filter((filename: string) => /^DDN.*\.xml$/.test(filename));

    switch (ddns.length) {
      case 0:
        this.importResultSvc.warning(this.translate.instant("comments.import.ddn.noDdn"));
        break;
      case 1:
        try {
          const ddn = this.importDdn(files[ddns[0]]);
          const ddnList = new Set<string>();

          ddn.querySelectorAll("dispatchFileName").forEach((dispatchFileName: Element) => {
            if (!dispatchFileName.textContent.startsWith("DDN")) {
              ddnList.add(dispatchFileName.textContent);
            }
          });

          return ddnList;
        } catch (error) {
          throw new DdnError(DDN_ERRORS.INVALID_STRUCTURE);
        }
      default:
        throw new DdnError(DDN_ERRORS.MULTIPLE_DDN);
    }
  }

  /**
   * Imports the attached media of the comment.
   *
   * @param {Comment} comment The comment to which to attach the given media.
   * @param {string} media The media to add to the given comment.
   * @param {Unzipped} files All the files in the ZIP.
   * @private
   */
  private async importAttachedMedia(
    comment: Comment,
    media: string,
    files: Unzipped
  ): Promise<Comment> {
    // create a note copy to be able to use this.update(old, new) later
    const updatedComment = { ...comment };
    // SPEC: If capability is false, do not import medias
    if (!this.commentService.canAttachMedia) {
      return Promise.resolve(updatedComment);
    }
    if (!Object.keys(files).includes(media)) {
      this.importResultSvc.warning(
        this.translate.instant("medias.import.error.media_missing", {
          media: media
        })
      );
      return Promise.resolve(updatedComment);
    }
    const mimeType = getMimeType(media);
    const mediaContent = new Blob([files[media].slice()], {
      type: mimeType
    });
    const fileToAdd = new UniversalFile([mediaContent], media, { type: mediaContent.type });
    return this.commentService
      .attachMediaToComment(fileToAdd, updatedComment, true)
      .then((attachedMedia: AttachedMedia) => {
        updatedComment.medias ??= [];
        updatedComment.medias.push(attachedMedia);
        return updatedComment;
      })
      .catch((err: Error) => {
        // SPEC: In case of an error on a media, still import the comment
        this.importResultSvc.warning(err.message);
        return updatedComment;
      });
  }

  /**
   * Checks the validity of an XML. For the XML to be considered valid, the
   * following conditions must be true:
   * - the main tag must be present;
   * - the code tag must be present;
   * - the XML must be parsed correctly.
   *
   * @param {string} xml The XML for which to check the validity.
   * @param {boolean} [isDdn] If the XML is a DDN (`false` by default).
   * @returns {boolean} `true` if the XML is valid, `false` otherwise.
   */
  private checkXmlIsValid(xml: Document, isDdn = false): boolean {
    const tags = isDdn ? ["ddn", "ddnCode"] : ["comment", "commentCode"];
    return (
      xml.getElementsByTagName("parsererror").length === 0 &&
      tags.every((tag: string) => !!xml.querySelector(tag))
    );
  }

  /**
   * Constructs the code of the comment from its `<commentCode>` tag. It also
   * checks that each attribute respects its format.
   *
   * @see {@link commentCodeAttributesChecks} for the formats.
   *
   * @param {Element} commentCodeElement The element from which to get the
   * attributes.
   * @throws `COMMENT_ERRORS.WRONG_ATTRIBUTE_FORMAT` if the format of an
   * attribute is wrong.
   * @returns {string} The comment code.
   */
  private constructCommentCode(commentCodeElement: Element): string {
    const attributes = [];

    for (const [attributeName, check] of this.commentCodeAttributesChecks) {
      const attributeValue = commentCodeElement.getAttribute(attributeName);
      if (!check(attributeValue)) {
        throw new CommentError(COMMENT_ERRORS.WRONG_ATTRIBUTE_FORMAT);
      }
      attributes.push(attributeValue);
    }

    return attributes.join("-").toUpperCase();
  }

  /**
   * Constructs the code of the comment from its `<ddnCode>` tag.
   *
   * @param {Element} ddnCodeElement The element from which to get the
   * attributes.
   * @returns {string} The DDN code.
   */
  private constructDdnCode(ddnCodeElement: Element): string {
    const modelIdentCode = ddnCodeElement.getAttribute("modelIdentCode");
    const senderIdent = ddnCodeElement.getAttribute("senderIdent");
    const receiverIdent = ddnCodeElement.getAttribute("receiverIdent");
    const yearOfDataIssue = ddnCodeElement.getAttribute("yearOfDataIssue");
    const seqNumber = ddnCodeElement.getAttribute("seqNumber");

    return `${modelIdentCode}-${senderIdent}-${receiverIdent}-${yearOfDataIssue}-${seqNumber}`.toUpperCase();
  }

  /**
   * Gets the location of the comment to import from its XML.
   * There are four possible comment locations:
   * - DM: `dmRefGroup`
   * - PM: `pmRefGroup`
   * - PUB: `pmRefGroup > externalPubCode > verbatim` (publication verbatim)
   * - APP: `pmRefgroup > externalPubCode > verbatim` (version of the app)
   *
   * @param {Document} commentXml The XML of the comment.
   * @returns {CommentLocation} The location of the comment.
   */
  private getLocation(commentXml: Document): CommentLocation {
    // The "dmRefGroup" tag is only present for comments on DM.
    if (commentXml.querySelector("dmRefGroup")) {
      return CommentLocation.DM;
    }

    /*
    The "externalPubCode" is not present for comments on DM and PM, but we
    already know that it is not a comment on a DM, so it can only be a
    comment on a PM.
    */
    if (!commentXml.querySelector("externalPubCode")) {
      return CommentLocation.PM;
    }

    /*
    The only difference between a comment on a publication and a comment on the
    application is the verbatimText content.
    For the application, the verbatim is the application version number.
    Thus, we check the format with a regex. If it matches, then it is a comment
    on the application. If not, the only other option still available is a
    comment on the publication.
    */
    const verbatimText = commentXml.querySelector("verbatimText").textContent;
    if (/^\d\.\d{1,3}\.\d{1,3}$/.test(verbatimText)) {
      return CommentLocation.APP;
    }
    return CommentLocation.PUB;
  }

  /**
   * Sets data that depends on the comment location.
   * This sets:
   * - reference;
   * - locationData;
   * - dmInfos/pmInfos/pubInfos/appInfos.
   *
   * @param {Document} commentXml The XML of the comment.
   * @param {Comment} comment The comment in creation.
   * @throws CommentError:
   * - `COMMENT_ERRORS.MISSING_DM` if the DM of the comment does not
   * exist **(only for DMs)**.
   * - `COMMENT_ERRORS.MISSING_PM` if the DM of the comment does not
   * exist **(only for PMs)**.
   */
  private setLocationInfo(commentXml: Document, comment: Comment): void {
    switch (comment.location) {
      case CommentLocation.DM:
        const dmc = this.constructDMCFull(commentXml);
        const duMeta = this.store.getDuMetaForComments(dmc);
        if (!duMeta) {
          throw new CommentError(COMMENT_ERRORS.MISSING_DM);
        }

        const dmTitle = duMeta?.shortTitle ?? "-";
        const dmIssueInfos = [
          duMeta?.issueNumber ?? "-",
          duMeta?.revision.toUpperCase() ?? "-",
          duMeta?.issueDate ?? "-"
        ].join(", ");

        comment.reference = duMeta.reference;
        comment.locationData = commentXml.querySelector("dmRefGroup")?.outerHTML;
        comment.dmInfos = {
          dmc: duMeta?.dmc ?? "-", // Because of DMs in multiple infosets.
          dmTitle,
          dmIssueInfos
        };
        break;
      case CommentLocation.PM:
        const pmTitle = commentXml.querySelector("pmTitle").textContent;
        if (!this.commentService.commentInfoMap.has(pmTitle)) {
          throw new CommentError(COMMENT_ERRORS.MISSING_PM);
        }

        const pmCodeElement = commentXml.querySelector("pmCode");
        const pmCode =
          [
            pmCodeElement.getAttribute("modelIdentCode"),
            pmCodeElement.getAttribute("pmIssuer"),
            pmCodeElement.getAttribute("pmNumber"),
            pmCodeElement.getAttribute("pmVolume")
          ].join("-") ?? "-";

        comment.reference = pmTitle;
        comment.locationData = commentXml.querySelector("pmRefGroup")?.outerHTML;
        comment.pmInfos = {
          pmCode
        };
        break;
      case CommentLocation.PUB:
        const language = this.store.pubInfo.lang;
        const verbatimText = this.store.pubInfo.verbatimText;

        const issueInfo = commentXml.querySelector("issueInfo");
        const issueNumber = issueInfo?.getAttribute("issueNumber") || "-";
        const inWork = issueInfo?.getAttribute("inWork") || "-";
        const reference = `${verbatimText}#${issueNumber}.${inWork}#${language}`;

        let issueDate = "-";
        const issueDateTag = commentXml.querySelector("pmRefAddressItems > issueDate");
        if (issueDateTag) {
          issueDate = [
            issueDateTag.getAttribute("year"),
            issueDateTag.getAttribute("month"),
            issueDateTag.getAttribute("day")
          ].join("-");
        }

        const pubCode = [
          this.store.pubInfo.model,
          this.store.pubInfo.revision,
          issueDate,
          this.store.pubInfo.lang
        ]
          .join("_")
          .toUpperCase();

        comment.reference = reference;
        comment.locationData = commentXml.querySelector("pmRefGroup")?.outerHTML;
        comment.pubInfos = {
          pubCode,
          verbatimText
        };
        break;
      case CommentLocation.APP:
        const appVersion = commentXml.querySelector("verbatimText")?.textContent || "-";

        comment.reference = `ORION2-v${appVersion}`;
        comment.locationData = commentXml.querySelector("pmRefGroup")?.outerHTML;
        comment.appInfos = {
          appName: "ORION2",
          appVersion
        };
        break;
      default:
        break;
    }
  }

  /**
   * **Only for comments on DM.**
   *
   * Constructs the full DMC of the comment to import from its XML.
   *
   * Example: DMC-JP-ALL-A001-05-10-3400-56A-0B1A-A_fr-FR
   *
   * @param {Document} commentXml The XML of the comment.
   * @returns {string} The full DMC of the comment.
   */
  private constructDMCFull(commentXml: Document): string {
    const dmCode = commentXml.querySelector("dmCode");

    const modelIdentCode = dmCode.getAttribute("modelIdentCode");
    const systemDiffCode = dmCode.getAttribute("systemDiffCode");
    const systemCode = dmCode.getAttribute("systemCode");

    const subSystemCode =
      dmCode.getAttribute("subSystemCode") + dmCode.getAttribute("subSubSystemCode");

    const assyCode = dmCode.getAttribute("assyCode");
    const disassyCode =
      dmCode.getAttribute("disassyCode") + dmCode.getAttribute("disassyCodeVariant");

    const infoCode = dmCode.getAttribute("infoCode") + dmCode.getAttribute("infoCodeVariant");
    const itemLocationCode = dmCode.getAttribute("itemLocationCode");

    const extensionCode = commentXml.querySelector("identExtension").getAttribute("extensionCode");
    const language = commentXml.querySelector("dmRefIdent language");
    const countryIsoCode = language.getAttribute("countryIsoCode");
    const languageIsoCode = language.getAttribute("languageIsoCode");

    return `DMC-${modelIdentCode}-${extensionCode}-${systemDiffCode}-${systemCode}-${subSystemCode}-${assyCode}-${disassyCode}-${infoCode}-${itemLocationCode}_${languageIsoCode}-${countryIsoCode}`;
  }

  ////////////////////////////////////////////////////////////
  ////////////////////// EXPORT SECTION //////////////////////
  ////////////////////////////////////////////////////////////

  /**
   * Exports the selection of comments.
   * The selection is exported inside a ZIP file, with each comment being an XML file.
   *
   * @param comments The current comment.
   * @param ddnData data added to the zip containing sender and receiver informations
   */
  public async exportSelection(comments: Comment[], ddnData?: DdnData): Promise<void> {
    if (!comments.length) {
      return;
    }

    const files: UniversalFile[] = [];
    for (const comment of comments) {
      if (!this.commentService.canManage(comment)) {
        continue;
      }
      const commentExportFiles = await this.exportComment(comment);
      files.push(...commentExportFiles);
    }
    if (!files.length) {
      return;
    }

    const filenames = files.map((xml: UniversalFile) => xml.name);
    if (ddnData) {
      const ddnFile = this.createDdnFile(ddnData, filenames);
      files.push(ddnFile);
    }
    this.downloadZip(files);
  }

  /**
   * Export a Comment as XML with its attached medias
   * The XML name is: COM-@model-@senderCageCode-@year-@increment-@commentType.xml
   *
   * "commentType" can be Q, I, or R.
   *
   * @param comment The comment for which to create the XML.
   * @returns The XML file of the comment.
   */
  private async exportComment(comment: Comment): Promise<UniversalFile[]> {
    const id = `COM-${comment.code}`;

    const xmlDom = document.implementation.createDocument("", "", null);
    xmlDom.appendChild(xmlDom.createProcessingInstruction("xml", 'version="1.0" encoding="utf-8"'));
    const commentElement = xmlDom.createElement("comment");
    commentElement.setAttribute("xmlns:xsi", "http://www.w3.org/2001/XMLSchema-instance");
    commentElement.setAttribute(
      "xsi:noNamespaceSchemaLocation",
      "http://www.s1000d.org/S1000D_4-1/xml_schema_flat/comment.xsd"
    );
    commentElement.setAttribute("id", id);
    xmlDom.appendChild(commentElement);

    // identAndStatusSection
    const identAndStatusElement = xmlDom.createElement("identAndStatusSection");
    identAndStatusElement.appendChild(this.getCommentAddress(comment));
    identAndStatusElement.appendChild(this.getCommentStatus(comment));
    commentElement.appendChild(identAndStatusElement);

    const mediaFiles = await this.exportMedias(comment, id);

    // commentContent
    const commentContentElement = this.getCommentContent(comment, mediaFiles);
    commentElement.appendChild(commentContentElement);

    const xmlString = new XMLSerializer().serializeToString(xmlDom).trim();
    const blob = new Blob([xmlString], { type: MimeType.XML });
    return [new UniversalFile([blob], `${id}.xml`, { type: MimeType.XML }), mediaFiles].flat();
  }

  /**
   * Create an XML file containing sender, receiver and comments informations.
   * The XML name is: DDN-@DDNCODE.xml
   * @param ddnData sender and receiver information displayed in XML elements or attributes
   * @param filenames comments filenames listed in deliveryListItem element
   * @returns The DDN XML file.
   */
  private createDdnFile(ddnData: DdnData, filenames: string[]): UniversalFile {
    // init xml document
    const xmlDom = document.implementation.createDocument("", "", null);
    xmlDom.appendChild(xmlDom.createProcessingInstruction("xml", 'version="1.0" encoding="utf-8"'));

    // create ddnIdent element
    const ddnIdent = xmlDom.createElement("ddnIdent");
    const ddnCode = xmlDom.createElement("ddnCode");
    ddnCode.setAttribute("modelIdentCode", ddnData.code.model);
    ddnCode.setAttribute("senderIdent", ddnData.code.senderIdent);
    ddnCode.setAttribute("receiverIdent", ddnData.code.receiverIdent);
    ddnCode.setAttribute("yearOfDataIssue", ddnData.code.year);
    ddnCode.setAttribute("seqNumber", ddnData.code.seqNumber);
    ddnIdent.appendChild(ddnCode);

    // create issueDate element
    const issueDate = xmlDom.createElement("issueDate");
    issueDate.setAttribute("year", ddnData.issueDate.year);
    issueDate.setAttribute("month", ddnData.issueDate.month);
    issueDate.setAttribute("day", ddnData.issueDate.day);

    // create dispatchAddress element
    const dispatchTo = xmlDom.createElement("dispatchTo");
    const dispatchFrom = xmlDom.createElement("dispatchFrom");
    const dispatchToAddress = this.getDispatchAddress(ddnData.receiverContact);
    const dispatchFromAddress = this.getDispatchAddress(ddnData.senderContact);
    dispatchTo.appendChild(dispatchToAddress);
    dispatchFrom.appendChild(dispatchFromAddress);

    // create ddnAddressItems element
    const ddnAddressItems = xmlDom.createElement("ddnAddressItems");
    ddnAddressItems.appendChild(issueDate);
    ddnAddressItems.appendChild(dispatchTo);
    ddnAddressItems.appendChild(dispatchFrom);

    // create ddnAddress element
    const ddnAddress = xmlDom.createElement("ddnAddress");
    ddnAddress.appendChild(ddnIdent);
    ddnAddress.appendChild(ddnAddressItems);

    // create ddnStatus element
    const ddnStatus = xmlDom.createElement("ddnStatus");
    const security = xmlDom.createElement("security");
    security.setAttribute("securityClassification", "01");
    const authorization = xmlDom.createElement("authorization");
    const brexDmRef = this.getBrexDmRef();
    const remarks = xmlDom.createElement("remarks");
    const simplePara = xmlDom.createElement("simplePara");
    simplePara.textContent = ddnData.remarks;
    remarks.appendChild(simplePara);
    ddnStatus.appendChild(security);
    ddnStatus.appendChild(authorization);
    ddnStatus.appendChild(brexDmRef);
    ddnStatus.appendChild(remarks);

    // create identAndStatusSection element
    const identAndStatusSection = xmlDom.createElement("identAndStatusSection");
    identAndStatusSection.appendChild(ddnAddress);
    identAndStatusSection.appendChild(ddnStatus);

    // create ddnContent element
    const ddnContent = xmlDom.createElement("ddnContent");
    const deliveryList = xmlDom.createElement("deliveryList");
    const code = Object.values(ddnData.code).join("-");
    const ddnFilename = `DDN-${code}.xml`;
    filenames.unshift(ddnFilename);
    filenames.forEach((filename: string) => {
      const deliveryListItem = xmlDom.createElement("deliveryListItem");
      const dispatchFileName = xmlDom.createElement("dispatchFileName");
      dispatchFileName.textContent = filename;
      deliveryListItem.appendChild(dispatchFileName);
      deliveryList.appendChild(deliveryListItem);
    });
    ddnContent.appendChild(deliveryList);

    // create ddn element
    const ddn = xmlDom.createElement("ddn");
    ddn.appendChild(identAndStatusSection);
    ddn.appendChild(ddnContent);
    xmlDom.appendChild(ddn);

    // create file
    const xmlString = new XMLSerializer().serializeToString(xmlDom).trim();
    return new UniversalFile([xmlString], ddnFilename, {
      type: MimeType.XML
    });
  }

  /**
   * Create the dispatchAddress elements containing address informations
   * @param address address informations
   * @returns dispatchAddress XML Node
   */
  private getDispatchAddress(contact: Contact): Element {
    const doc = document.implementation.createDocument("", "", null);
    const dispatchAddress = doc.createElement("dispatchAddress");
    const enterprise = doc.createElement("enterprise");
    const enterpriseName = doc.createElement("enterpriseName");
    const address = doc.createElement("address");
    const street = doc.createElement("street");
    const postalZipCode = doc.createElement("postalZipCode");
    const city = doc.createElement("city");
    const country = doc.createElement("country");
    const state = doc.createElement("state");
    const phoneNumber = doc.createElement("phoneNumber");
    const email = doc.createElement("email");
    enterpriseName.textContent = contact.entrepriseName;
    enterprise.appendChild(enterpriseName);
    street.textContent = contact.street;
    postalZipCode.textContent = contact.postal;
    city.textContent = contact.city;
    country.textContent = contact.country;
    state.textContent = contact.state;
    phoneNumber.textContent = contact.phone;
    email.textContent = contact.email;
    address.appendChild(street);
    address.appendChild(postalZipCode);
    address.appendChild(city);
    address.appendChild(country);
    address.appendChild(state);
    address.appendChild(phoneNumber);
    address.appendChild(email);
    dispatchAddress.appendChild(enterprise);
    dispatchAddress.appendChild(address);
    return dispatchAddress;
  }

  /**
   * Create the brexDmRef element containing model information
   * Only depend of the model
   * @returns brexDmRef XML Node
   */
  private getBrexDmRef(): Element {
    const doc = document.implementation.createDocument("", "", null);
    const brexDmRef = doc.createElement("brexDmRef");
    const dmRef = doc.createElement("dmRef");
    const dmRefIdent = doc.createElement("dmRefIdent");
    const dmCode = doc.createElement("dmCode");
    dmCode.setAttribute("assyCode", "0000");
    dmCode.setAttribute("disassyCode", "00");
    dmCode.setAttribute("disassyCodeVariant", "A");
    dmCode.setAttribute("infoCode", "022");
    dmCode.setAttribute("infoCodeVariant", "A");
    dmCode.setAttribute("itemLocationCode", "D");
    dmCode.setAttribute("modelIdentCode", this.store.pubInfo.model);
    dmCode.setAttribute("subSubSystemCode", "0");
    dmCode.setAttribute("subSystemCode", "0");
    dmCode.setAttribute("systemCode", "00");
    dmCode.setAttribute("systemDiffCode", "A000");
    const language = doc.createElement("language");
    language.setAttribute("countryIsoCode", "US");
    language.setAttribute("languageIsoCode", "en");
    dmRefIdent.appendChild(dmCode);
    dmRefIdent.appendChild(language);
    dmRef.appendChild(dmRefIdent);
    brexDmRef.appendChild(dmRef);
    return brexDmRef;
  }

  /**
   * Returns the commentAddress block.
   *
   * @param comment The current comment.
   * @return {Element} commentAddress XML Node
   */
  private getCommentAddress(comment: Comment): Element {
    const doc = document.implementation.createDocument("", "", null);
    const commentAddress = doc.createElement("commentAddress");

    const commentIdent = doc.createElement("commentIdent");
    const commentCode = doc.createElement("commentCode");
    commentCode.setAttribute("modelIdentCode", comment.model);
    commentCode.setAttribute("senderIdent", comment.cageCode);
    commentCode.setAttribute("yearOfDataIssue", comment.year);
    commentCode.setAttribute("seqNumber", comment.seqNumber);
    commentCode.setAttribute("commentType", comment.commentType);
    commentIdent.appendChild(commentCode);
    const language = doc.createElement("language");
    language.setAttribute("languageIsoCode", this.store.pubInfo.lang);
    const country = this.store.pubInfo.packageId.slice(-2).toUpperCase();
    language.setAttribute("countryIsoCode", country);
    commentIdent.appendChild(language);
    commentAddress.appendChild(commentIdent);
    const commentAddressItems = doc.createElement("commentAddressItems");
    const commentTitle = doc.createElement("commentTitle");
    commentTitle.textContent = comment.title;
    commentAddressItems.appendChild(commentTitle);
    const issueDate = doc.createElement("issueDate");

    // "comment.date" is typed as a "Date", but it is only true at creation.
    // After it is saved as a string. Thus, we need to create a new date.
    const [year, month, day] = this.datePipe
      .transform(new Date(comment.date), "yyyy_MM_dd")
      .split("_");
    issueDate.setAttribute("year", year);
    issueDate.setAttribute("month", month);
    issueDate.setAttribute("day", day);
    commentAddressItems.appendChild(issueDate);
    commentAddress.appendChild(commentAddressItems);

    return commentAddress;
  }

  /**
   * Returns the commentStatus block.
   *
   * @param comment The current comment.
   * @return {Element} commentStatus XML node
   */
  private getCommentStatus(comment: Comment): Element {
    const doc = document.implementation.createDocument("", "", null);
    const commentStatus = doc.createElement("commentStatus");
    const security = doc.createElement("security");
    // SPEC: Always "01".
    security.setAttribute("securityClassification", "01");
    commentStatus.appendChild(security);
    const commentPriority = doc.createElement("commentPriority");
    commentPriority.setAttribute("commentPriorityCode", comment.priority);
    commentStatus.appendChild(commentPriority);
    if (comment.responseType && comment.responseType !== "default") {
      const commentResponse = doc.createElement("commentResponse");
      commentResponse.setAttribute("responseType", comment.responseType);
      commentStatus.appendChild(commentResponse);
    }
    const commentRefs = doc.createElement("commentRefs");
    const locationData = this.domParser.parseFromString(comment.locationData, "text/xml");
    commentRefs.appendChild(locationData.documentElement);
    commentStatus.appendChild(commentRefs);

    return commentStatus;
  }

  /**
   * Returns the commentContent block.
   * It creates a simplePara element for every paragraph in the comment.
   *
   * @param comment The current comment.
   * @param mediaFiles Current comment media files
   * @return {Element} commentContent XML Node
   */
  private getCommentContent(comment: Comment, mediaFiles: UniversalFile[]): Element {
    const doc = document.implementation.createDocument("", "", null);
    const commentContent = doc.createElement("commentContent");
    comment.contents.forEach((simplePara: string) => {
      const simpleParaElement = doc.createElement("simplePara");
      simpleParaElement.textContent = simplePara;
      commentContent.appendChild(simpleParaElement);
    });
    mediaFiles.forEach((mediaFile: UniversalFile) => {
      const mediaElement = doc.createElement("attachmentRef");

      const [_, attachmentNumber, fileExtension] = mediaFile.name.split(this.mediaNameRegex);
      mediaElement.setAttribute("attachmentNumber", attachmentNumber);
      mediaElement.setAttribute("fileExtension", fileExtension);
      commentContent.appendChild(mediaElement);
    });

    return commentContent;
  }

  /**
   * Export comment's medias
   * @param comment
   * @param commentId
   * @private
   */
  private exportMedias(comment: Comment, commentId: string): Promise<UniversalFile[]> {
    if (!this.commentService.canAttachMedia) {
      return Promise.resolve([]);
    }
    return Promise.all(
      (comment.medias || []).map((media: AttachedMedia, mediaIndex: number) => {
        const mediaNumber = (mediaIndex + 1).toString().padStart(2, "0");
        return this.commentService
          .getAttachedMedia(media.id, media.title)
          .then((mediaFile: AttachedMediaFile) => {
            const blob = "data" in mediaFile.file ? mediaFile.file.data : mediaFile.file;
            const ext = media.title.split(".").pop();
            // SPEC: media file is named like :
            // "COM-@model-@senderCageCode-@year-@increment-@commentType-@mediaNumber.@mediaExt"
            return new UniversalFile([blob], `${commentId}-${mediaNumber}.${ext}`, {
              type: mediaFile.file.type
            });
          });
      })
    );
  }

  /**
   * Downloads a list of given XMLs inside a ZIP file.
   *
   * The ZIP name is: COMMENT_@pubTitle_@revision_@userExporting_@date.zip
   *
   * @param {UniversalFile[]} xmls The xmls to download.
   */
  private downloadZip(xmls: UniversalFile[]): void {
    const zipName = [
      "COMMENT",
      getIncrementalPackageId(this.store.publicationID),
      this.store.pubInfo.revision,
      this.store.user.samAccountName,
      this.datePipe.transform(new Date(), "yyyy_MM_dd")
    ].join("_");

    const xmlsToDownload = xmls.map(
      (xml: UniversalFile) => new File([xml.data], xml.name, { type: xml.type })
    );
    this.downloadService.download(xmlsToDownload, zipName, null, true);
  }
}
